// InstallAppCard
//
// Small settings card that surfaces PWA install eligibility. When the browser
// fires `beforeinstallprompt`, we stash the event and show a prominent
// "Install Selah" button that opens the native install dialog on
// Windows 11 / macOS / ChromeOS / Android. On iOS Safari (no PWA API),
// we render a lightweight guide instead.
//
// IMPORTANT — App Store compliance:
//   Inside the native iOS App Store build (WKWebView wrapper), this card
//   MUST NOT render. It would tell an already-installed reviewer to
//   "Install Selah Downloads on this device" and reference macOS/Windows
//   install steps — both are Guideline 3.1.1 anti-steering violations
//   (pointing the user off-store) and Guideline 2.1(b) app-completeness
//   violations (offering a redundant, non-functional install prompt).
//   Apple's Aug 21 2026 reviewer flagged the subscription page for exactly
//   this class of issue. We now return null on iOS.

import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { DownloadCloud, Info, Check, Smartphone, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { isIOS } from '../utils/platform';

const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') return false;
  // iOS reports via navigator.standalone; everyone else uses matchMedia.
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Win/i.test(ua)) return 'windows';
  if (/Mac/i.test(ua)) return 'mac';
  return 'other';
};

const InstallAppCard = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandaloneDisplay());
  const platform = detectPlatform();
  // Hard bail-out on iOS App Store builds — see file header for rationale.
  // Placed AFTER hooks so React's rules of hooks aren't violated.
  const suppressForIOSAppStore = isIOS();

  useEffect(() => {
    const onBeforeInstall = (e) => {
      // Prevent Chrome/Edge default mini-infobar
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      toast.success('Selah installed');
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('Great — installing Selah…');
      }
      setDeferredPrompt(null);
    } catch (e) {
      console.error('install prompt error', e);
      toast.error('Install was cancelled');
    }
  };

  const PlatformIcon =
    platform === 'ios' || platform === 'android' ? Smartphone : Monitor;

  // Suppress the entire card inside the iOS App Store build. Placed here
  // (after all hooks) to respect React's rules of hooks.
  if (suppressForIOSAppStore) return null;

  return (
    <Card
      className="glass-card p-6 sm:p-8"
      data-testid="install-app-card"
    >
      <div className="flex items-start gap-3">
        <PlatformIcon size={22} className="text-[var(--sd-primary)] mt-0.5" />
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-[var(--sd-fg)]">
            Install Selah Downloads on this device
          </h2>
          <p className="text-sm text-[var(--sd-muted-fg)] mt-1 max-w-xl">
            Pin Selah to your taskbar, dock, or home screen. Opens in one tap,
            runs offline for the shell, and lets you set a global shortcut on
            Windows 11 / macOS.
          </p>

          {installed ? (
            <div className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--sd-fg)] bg-[var(--sd-primary)]/15 border border-[var(--sd-primary)]/40 px-3 py-2 rounded-lg">
              <Check size={16} className="text-[var(--sd-primary)]" />
              Selah Downloads is installed as an app on this device.
            </div>
          ) : deferredPrompt ? (
            <Button
              onClick={handleInstall}
              className="mt-4 bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)]"
              data-testid="install-app-button"
            >
              <DownloadCloud size={16} className="mr-2" />
              Install Selah Downloads
            </Button>
          ) : platform === 'ios' ? (
            <div className="mt-4 text-sm text-[var(--sd-fg-secondary)] p-3 rounded-lg border border-[var(--sd-border)] bg-[var(--sd-surface)]">
              <p className="flex items-start gap-2">
                <Info size={14} className="mt-0.5 text-[var(--sd-primary)]" />
                <span>
                  On iOS: tap the <span className="text-[var(--sd-fg)]">Share</span> button in Safari, then choose
                  <span className="text-[var(--sd-fg)]"> Add to Home Screen</span>. Selah will run like a native app.
                </span>
              </p>
            </div>
          ) : (
            <div className="mt-4 text-sm text-[var(--sd-fg-secondary)] p-3 rounded-lg border border-[var(--sd-border)] bg-[var(--sd-surface)]">
              <p className="flex items-start gap-2">
                <Info size={14} className="mt-0.5 text-[var(--sd-primary)]" />
                <span>
                  Your browser hasn&apos;t offered an install prompt yet. In Chrome or Edge,
                  look for the install icon in the address bar, or open the browser menu
                  and choose <span className="text-[var(--sd-fg)]">Install app…</span>
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default InstallAppCard;
